import { useCallback, useEffect, useState } from 'react';

export type AppTheme = 'light' | 'dark';

const DEFAULT_THEME: AppTheme = 'dark';
const THEME_STORAGE_KEY = 'notification-service-theme';

function isAppTheme(value: string | null): value is AppTheme {
  return value === 'light' || value === 'dark';
}

function buildThemeStorageKeys(operatorLoginId: number | null): string[] {
  const keys = [THEME_STORAGE_KEY];

  if (typeof operatorLoginId === 'number' && operatorLoginId > 0) {
    keys.unshift(`${THEME_STORAGE_KEY}:${operatorLoginId}`);
  }

  return keys;
}

/** Применяет тему к корневому DOM-узлу и нативным элементам формы. */
function applyThemeAttribute(theme: AppTheme): void {
  if (typeof document === 'undefined') {
    return;
  }

  document.documentElement.setAttribute('data-theme', theme);
  document.documentElement.style.colorScheme = theme;
}

/** Возвращает сохраненную тему оператора или общий fallback для экрана входа. */
function readStoredTheme(operatorLoginId: number | null): AppTheme {
  if (typeof window === 'undefined') {
    return DEFAULT_THEME;
  }

  for (const storageKey of buildThemeStorageKeys(operatorLoginId)) {
    const savedTheme = window.localStorage.getItem(storageKey);

    if (isAppTheme(savedTheme)) {
      return savedTheme;
    }
  }

  return DEFAULT_THEME;
}

/** Сохраняет тему как глобальный fallback и как персональную настройку оператора. */
function persistTheme(theme: AppTheme, operatorLoginId: number | null): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(THEME_STORAGE_KEY, theme);

  if (typeof operatorLoginId === 'number' && operatorLoginId > 0) {
    window.localStorage.setItem(`${THEME_STORAGE_KEY}:${operatorLoginId}`, theme);
  }
}

/** Кастомный хук по управлению темой интерфейса оператора. */
export const useTheme = (operatorLoginId: number | null) => {
  const [theme, setThemeState] = useState<AppTheme>(DEFAULT_THEME);

  useEffect(() => {
    const savedTheme = readStoredTheme(operatorLoginId);

    setThemeState(savedTheme);
    applyThemeAttribute(savedTheme);
  }, [operatorLoginId]);

  /** Явно устанавливает тему и сразу сохраняет выбор оператора. */
  const setTheme = useCallback((nextTheme: AppTheme) => {
    setThemeState(nextTheme);
    persistTheme(nextTheme, operatorLoginId);
    applyThemeAttribute(nextTheme);
  }, [operatorLoginId]);

  /** Переключает тему между светлой и темной. */
  const toggleTheme = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [setTheme, theme]);

  return { theme, setTheme, toggleTheme };
};